import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import { FaSearch, FaEye } from "react-icons/fa";
import axios from "axios";
import Select from "react-select";

import { gbState } from "../../../components/shared/GlobalState";

const AssetSettlementHistoryChild = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [unfilteredData, setUnfilteredData] = useState([]);
  const [settlementOptions, setSettlementOptions] = useState([]);
  const [dropDownSettlement, setDropDownSettlement] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [{ user }] = useAtom(gbState);

  const getDataFromServer = () => {
    let userToken = localStorage.getItem("token");

    let urlLink = `${process.env.REACT_APP_API_URL}/church/stocksettle/${user.church_id}`;

    const config = {
      method: "GET",
      url: urlLink,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };
    axios(config)
      .then(function (response) {
        //console.log(JSON.stringify(response.data.users));
        const oldData = response.data.settled_stock;
        const updatedData = oldData.map((data) => {
          return {
            //   ...data,
            id: data?._id,
            settlement_id: data?.settled_stock?.settlement_id,
            settlement_date: data?.settled_stock?.settlement_date,
            asset_id: data?.settled_stock?.asset_id?._id,
            item_code: data?.settled_stock?.asset_id?.item_code,
            asset_name: data?.settled_stock?.asset_id?.asset_name,
            opening_stock: data?.settled_stock?.opening_stock,
            inward_qty: data?.settled_stock?.inward_qty,
            outward_qty: data?.settled_stock?.outward_qty,
            closing_stock: data?.settled_stock?.closing_stock,
          };
        });

        setData(updatedData);
        setUnfilteredData(updatedData);
      })
      .catch(function (err) {
        console.log("error api call ", err);
      });
  };

  useEffect(() => {
    const getDataFromServer = () => {
      let userToken = localStorage.getItem("token");

      let urlLink = `${process.env.REACT_APP_API_URL}/church/stocksettle/${user.church_id}`;

      const config = {
        method: "GET",
        url: urlLink,
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      };
      axios(config)
        .then(function (response) {
          //console.log(JSON.stringify(response.data.users));
          const oldData = response.data.settled_stock;
          const updatedData = oldData.map((data) => {
            return {
              //   ...data,
              id: data?._id,
              settlement_id: data?.settled_stock?.settlement_id,
              settlement_date: data?.settled_stock?.settlement_date,
              asset_id: data?.settled_stock?.asset_id?._id,
              item_code: data?.settled_stock?.asset_id?.item_code,
              asset_name: data?.settled_stock?.asset_id?.asset_name,
              opening_stock: data?.settled_stock?.opening_stock,
              inward_qty: data?.settled_stock?.inward_qty,
              outward_qty: data?.settled_stock?.outward_qty,
              closing_stock: data?.settled_stock?.closing_stock,
            };
          });

          setData(updatedData);
          setUnfilteredData(updatedData);
        })
        .catch(function (err) {
          console.log("error api call ", err);
        });
    };

    getDataFromServer();
  }, []);

  useEffect(() => {
    // Create an array of unique settlements based on settlement_id
    const uniqueSettlements = data.filter(
      (settlement, index, self) =>
        index ===
        self.findIndex((s) => s.settlement_id === settlement.settlement_id)
    );

    // Map the unique settlements to the desired format
    const settlementOptions = uniqueSettlements.map((settlement) => ({
      value: settlement.settlement_id,
      label: `${settlement.settlement_id}\n${
        settlement.settlement_date?.split("T")[0]
      }`,
    }));

    setSettlementOptions(settlementOptions);
  }, [unfilteredData]);

  const handleSearch = (e) => {
    setDropDownSettlement(e?.value);
  };

  useEffect(() => {
    if (dropDownSettlement) {
      setData(
        unfilteredData?.filter((item) => {
          return item?.settlement_id === dropDownSettlement;
        })
      );
    } else {
      setData(unfilteredData);
    }
  }, [dropDownSettlement, unfilteredData]);

  const filteredData = useMemo(() => {
    return data.filter((item) =>
      Object.values(item).some((value) =>
        value.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [data, searchTerm]);

  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  return (
    <div className="w-full px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          Asset Settlement History
        </h2>
      </div>

      <div className="flex flex-col mb-4 space-y-4 sm:flex-row sm:space-y-0 sm:space-x-4">
        <Select
          options={settlementOptions}
          onChange={handleSearch}
          placeholder="Select Settlement ID..."
          isClearable
          className="w-full sm:w-64"
        />
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search assets..."
          InputProps={{
            startAdornment: <FaSearch className="mr-2 text-gray-400" />,
          }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full sm:w-64"
        />
      </div>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Settlement Code</TableCell>
              <TableCell>Asset Code</TableCell>
              <TableCell align="center">Opening Stock</TableCell>
              <TableCell align="center">Inward QTY</TableCell>
              <TableCell align="center">Outward QTY</TableCell>
              <TableCell align="center">Closing Stock</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <div>
                    <div>{row.settlement_id}</div>
                    <div className="text-xs text-gray-500">
                      {row.settlement_date?.split("T")[0]}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div>
                    <div>{row.asset_name}</div>
                    <div className="text-xs text-gray-500">{row.item_code}</div>
                  </div>
                </TableCell>
                <TableCell align="center">{row.opening_stock}</TableCell>
                <TableCell align="center">{row.inward_qty}</TableCell>
                <TableCell align="center">{row.outward_qty}</TableCell>
                <TableCell align="center">{row.closing_stock}</TableCell>
                <TableCell>
                  <Tooltip title="View details">
                    <IconButton
                      size="small"
                      onClick={() => {
                        // Add view details functionality
                      }}
                    >
                      <FaEye className="text-blue-600" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination controls */}
      <div className="flex items-center justify-between mt-4">
        <div>
          <span>Rows per page: </span>
          <select
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(Number(e.target.value))}
          >
            {[5, 10, 25, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Button
            disabled={page === 0}
            onClick={() => setPage((prev) => prev - 1)}
          >
            Previous
          </Button>
          <Button
            disabled={paginatedData.length < rowsPerPage}
            onClick={() => setPage((prev) => prev + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AssetSettlementHistoryChild;
